/**
 * useJurisdictionOrder — the user's preferred order, and how it changes.
 *
 * The picker already renders in the right order without this: the backend
 * returns available-sources rows pre-sorted. This hook owns the REORDER
 * interaction — the sequence being dragged, saving the whole of it, and
 * reconciling with what the server actually stored.
 *
 * A reorder applies immediately and is saved behind it. A failed save reverts
 * to the last known-good sequence rather than leaving the list showing an
 * order that was never persisted.
 *
 * REORDERING IS GATED ON THE PREFERENCE LOADING. Until the endpoint's
 * migration is applied it errors, and a drag would then be a gesture into a
 * void: it looks like it worked, and the order is gone on reload. `canReorder`
 * stays false until a GET actually succeeds, so the picker renders read-only
 * instead, and turns itself on the moment the endpoint answers.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '../../store';
import {
  researchGetJurisdictionOrder,
  researchSetJurisdictionOrder,
} from '../../store/actions/ChatAction';
import {
  applyOrder,
  fullSequence,
  moveJurisdiction,
  normaliseOrder,
  reconcileOrder,
} from './jurisdictionOrder';

export interface UseJurisdictionOrderResult {
  /** The stored preference, as last confirmed by the server. */
  order: string[];
  /** Sort the codes a caller is showing by that preference. */
  sort: (codes: string[]) => string[];
  /** Move a jurisdiction and persist the whole sequence. */
  reorder: (visible: string[], code: string, toIndex: number) => Promise<string[]>;
  isSaving: boolean;
  /**
   * Is the preference actually storable?
   *
   * False until the GET succeeds — the drag must not be offered when it
   * cannot persist.
   */
  canReorder: boolean;
  /** The GET has not resolved either way yet. */
  isLoading: boolean;
}

export function useJurisdictionOrder(): UseJurisdictionOrderResult {
  const dispatch = useAppDispatch();
  const [order, setOrder] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [canReorder, setCanReorder] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  /** The last sequence the server confirmed — what a failed save reverts to. */
  const committedRef = useRef<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    // Wrapped: a preference that cannot be read must leave the picker in
    // default order, never break it.
    Promise.resolve(dispatch(researchGetJurisdictionOrder()))
      .then((stored) => {
        if (cancelled) return;
        const clean = normaliseOrder(stored as string[]);
        committedRef.current = clean;
        setOrder(clean);
        // The endpoint answered, so the preference can be stored.
        setCanReorder(true);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [dispatch]);

  const reorder = useCallback(
    async (visible: string[], code: string, toIndex: number): Promise<string[]> => {
      // Belt and braces: the UI does not offer the drag, and the hook will
      // not act on one either.
      if (!canReorder) return committedRef.current;

      const moved = moveJurisdiction(visible, code, toIndex);
      // The WHOLE sequence: the server stores what it is sent, so anything
      // omitted would be dropped. Codes the user cannot see are carried.
      const sequence = fullSequence(moved, committedRef.current);

      // Optimistic: the list settles under the pointer, not after a round trip.
      setOrder(sequence);
      setIsSaving(true);

      try {
        const result = await Promise.resolve(dispatch(researchSetJurisdictionOrder(sequence)));
        if (!result) {
          // Never persisted — go back to what is actually stored.
          setOrder(committedRef.current);
          return committedRef.current;
        }
        // What the server stored wins; it may have ignored retired keys.
        const confirmed = reconcileOrder(sequence, result.order);
        committedRef.current = confirmed;
        setOrder(confirmed);
        return confirmed;
      } catch {
        setOrder(committedRef.current);
        return committedRef.current;
      } finally {
        setIsSaving(false);
      }
    },
    [canReorder, dispatch]
  );

  return {
    order,
    sort: useCallback((codes: string[]) => applyOrder(codes, order), [order]),
    reorder,
    isSaving,
    canReorder,
    isLoading,
  };
}

export default useJurisdictionOrder;
